/**
 * A workspace's branch and commit formats (§5.4) — the same field the app's
 * Git & merge page edits, so an administrator reading the workspace here sees
 * and can change the convention members' agents follow.
 *
 * Workspace-only, like the three lists: an organization does not default it
 * (`overridableSettings` leaves it out), so there is nothing to inherit and no
 * Reset. Presets, templates and examples are read from
 * `@airlock/contracts/settings/git-formats`, never repeated here.
 */
import type React from 'react';
import {
  CUSTOM_FORMAT,
  MAX_FORMAT_GUIDANCE,
  MAX_FORMAT_TEMPLATE,
  formatExample,
  presetsFor,
  type GitFormat,
  type GitFormatKind,
  type GitFormats
} from '@airlock/contracts/settings/git-formats';
import { color, font, radius } from '../../theme/tokens';
import { Field, Input } from '../../components/primitives';
import { selectStyle } from './shared';

/** The keys this editor answers for, beside the lists' (`settings-coverage.test.ts`). */
export const WORKSPACE_FORMAT_SETTINGS = ['gitFormats'] as const;

const NONE = 'none';
const LABEL: Record<GitFormatKind, string> = { branch: 'Branch name format', commit: 'Commit message format' };

export function GitFormatsField({
  value,
  onChange,
  editable
}: {
  value: GitFormats | null;
  onChange: (next: GitFormats | null) => void;
  editable: boolean;
}): React.JSX.Element {
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 640 }}>
      {(['branch', 'commit'] as const).map((kind) => (
        <FormatRow key={kind} kind={kind} formats={value} onChange={onChange} editable={editable} />
      ))}
    </div>
  );
}

function FormatRow({
  kind,
  formats,
  onChange,
  editable
}: {
  kind: GitFormatKind;
  formats: GitFormats | null;
  onChange: (next: GitFormats | null) => void;
  editable: boolean;
}): React.JSX.Element {
  const format = formats?.[kind] ?? null;
  const set = (next: GitFormat | null): void => {
    const merged: GitFormats = { branch: formats?.branch ?? null, commit: formats?.commit ?? null, [kind]: next };
    onChange(merged.branch || merged.commit ? merged : null);
  };
  const id = `git-format-${kind}`;
  const example = format ? formatExample(kind, format) : '';
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <Field label={LABEL[kind]} htmlFor={id}>
        <select
          id={id}
          disabled={!editable}
          value={format?.preset ?? NONE}
          onChange={(event) => {
            const preset = event.target.value;
            if (preset === NONE) return set(null);
            set({ preset, template: preset === CUSTOM_FORMAT ? format?.template ?? '' : '', guidance: format?.guidance ?? '' });
          }}
          style={selectStyle}
        >
          <option value={NONE}>No format</option>
          {presetsFor(kind).map((one) => (
            <option key={one.id} value={one.id}>
              {one.label} — {one.template}
            </option>
          ))}
          <option value={CUSTOM_FORMAT}>Custom template</option>
        </select>
      </Field>
      {format?.preset === CUSTOM_FORMAT && (
        <Input
          aria-label={`${LABEL[kind]} template`}
          disabled={!editable}
          value={format.template}
          maxLength={MAX_FORMAT_TEMPLATE}
          placeholder="{type}/[{ticket}-]{description}"
          onChange={(event) => set({ ...format, template: event.target.value })}
          style={{ fontFamily: font.mono, fontSize: 13 }}
        />
      )}
      {format && (
        <textarea
          aria-label={`${LABEL[kind]} guidance`}
          disabled={!editable}
          value={format.guidance}
          maxLength={MAX_FORMAT_GUIDANCE}
          placeholder="Anything else the agent should follow (optional)"
          onChange={(event) => set({ ...format, guidance: event.target.value })}
          style={{
            width: '100%',
            minHeight: 56,
            fontFamily: 'inherit',
            fontSize: 13,
            padding: '9px 12px',
            border: `1px solid ${color.lineInput}`,
            borderRadius: radius.lg,
            background: color.page,
            color: color.ink
          }}
        />
      )}
      {example && (
        <div style={{ fontSize: 12, color: color.inkFaint }}>
          For example <span style={{ fontFamily: font.mono, color: color.ink }}>{example}</span>
        </div>
      )}
    </div>
  );
}
